import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Drop, CaretRight, PencilSimple, Copy, Lightning, Clock, Wrench } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const empty = () => ({
  name: "",
  output_product_id: "",
  peso_grammi: 0,
  material_id: "",
  cost_per_kg: 0,
  produzione_oraria: 0,
  costo_orario_lavoro: 0,
  kwh_orari: 0,
  costo_kwh: 0,
  scarto_percentuale: 0,
  components: [],
  notes: "",
});

export default function Plastiche() {
  const [formulas, setFormulas] = useState([]);
  const [products, setProducts] = useState([]);
  const [costs, setCosts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState(empty());

  const load = useCallback(async () => {
    try {
      const [f, p, c] = await Promise.all([
        api.get("/plastic-formulas"),
        api.get("/products", { params: { business_unit: "axelplast" } }),
        api.get("/plastic-formulas/costs/all"),
      ]);
      setFormulas(f.data);
      setProducts(p.data);
      setCosts(c.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const productById = (id) => products.find((p) => p.id === id);
  const costById = (id) => costs.find((c) => c.formula_id === id);

  const outputCandidates = products.filter(
    (p) => p.business_unit === "axelplast" && p.type === "prodotto_finito"
  );
  const materialCandidates = products.filter(
    (p) => p.business_unit === "axelplast" && p.type === "materia_prima"
  );
  const componentCandidates = products.filter(
    (p) => p.business_unit === "axelplast" &&
      ["materia_prima", "imballaggio", "semilavorato"].includes(p.type)
  );

  // Live cost preview based on form values (for the dialog)
  const livePreview = (() => {
    const peso = Number(form.peso_grammi) || 0;
    const cpk = Number(form.cost_per_kg) || 0;
    const ph = Number(form.produzione_oraria) || 0;
    const cl = Number(form.costo_orario_lavoro) || 0;
    const kwh = Number(form.kwh_orari) || 0;
    const ck = Number(form.costo_kwh) || 0;
    const scarto = Number(form.scarto_percentuale) || 0;
    const material = (peso * cpk / 1000) * (1 + scarto / 100);
    const labor = ph > 0 ? cl / ph : 0;
    const electric = ph > 0 ? (kwh * ck) / ph : 0;
    const compTotal = (form.components || []).reduce((s, c) => {
      const p = productById(c.product_id);
      const unit = Number(p?.purchase_cost) || 0;
      return s + (Number(c.quantity) || 0) * unit;
    }, 0);
    return { material, labor, electric, compTotal, total: material + labor + electric + compTotal };
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.output_product_id) return toast.error("Seleziona il prodotto plastico");
    if (!form.material_id) return toast.error("Seleziona la materia prima");
    if (!form.peso_grammi) return toast.error("Inserisci il peso del pezzo (g)");
    if (!form.produzione_oraria) return toast.error("Inserisci la produzione oraria");
    const payload = {
      name: form.name,
      output_product_id: form.output_product_id,
      peso_grammi: Number(form.peso_grammi),
      material_id: form.material_id,
      cost_per_kg: Number(form.cost_per_kg),
      produzione_oraria: Number(form.produzione_oraria),
      costo_orario_lavoro: Number(form.costo_orario_lavoro),
      kwh_orari: Number(form.kwh_orari),
      costo_kwh: Number(form.costo_kwh),
      scarto_percentuale: Number(form.scarto_percentuale),
      components: form.components.map((c) => ({
        product_id: c.product_id,
        quantity: Number(c.quantity),
        unit: c.unit || "pz",
      })),
      notes: form.notes,
    };
    try {
      if (editingId) {
        await api.put(`/plastic-formulas/${editingId}`, payload);
        toast.success("Distinta aggiornata");
      } else {
        await api.post("/plastic-formulas", payload);
        toast.success("Distinta creata");
      }
      setCreating(false);
      setEditingId(null);
      setForm(empty());
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (f) => {
    setForm({
      name: f.name,
      output_product_id: f.output_product_id,
      peso_grammi: f.peso_grammi,
      material_id: f.material_id,
      cost_per_kg: f.cost_per_kg,
      produzione_oraria: f.produzione_oraria,
      costo_orario_lavoro: f.costo_orario_lavoro,
      kwh_orari: f.kwh_orari,
      costo_kwh: f.costo_kwh,
      scarto_percentuale: f.scarto_percentuale || 0,
      components: (f.components || []).map((c) => ({
        product_id: c.product_id,
        quantity: c.quantity,
        unit: c.unit || "pz",
      })),
      notes: f.notes || "",
    });
    setEditingId(f.id);
    setCreating(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la distinta?")) return;
    try {
      await api.delete(`/plastic-formulas/${id}`);
      toast.success("Distinta eliminata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const duplicate = async (f) => {
    if (!window.confirm(`Duplicare "${f.name}"?`)) return;
    try {
      await api.post(`/plastic-formulas/${f.id}/duplicate`);
      toast.success("Distinta duplicata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateComponent = (i, key, value) => {
    const next = [...form.components];
    next[i] = { ...next[i], [key]: value };
    if (key === "product_id") {
      const p = products.find((x) => x.id === value);
      next[i].unit = p?.unit || "pz";
    }
    setForm({ ...form, components: next });
  };

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="plastiche-page">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Axelplast · Distinte di Lavorazione
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Lavorazioni Plastiche
          </h1>
          <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
            Calcolo costo per pezzo: materia prima, manodopera, energia elettrica e accessori.
          </p>
        </div>
        <button
          onClick={() => { setForm(empty()); setEditingId(null); setCreating(true); }}
          data-testid="new-plastic-formula-btn"
          className="bg-[#F4A261] hover:bg-[#E08C4A] text-white px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
        >
          <Plus size={14} weight="bold" /> Nuova Distinta Plastica
        </button>
      </div>

      {formulas.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <Drop size={32} weight="duotone" color="#F4A261" className="mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">
            Nessuna distinta plastica. Crea la prima per calcolare il costo per pezzo (HDPE + lavoro + elettricità).
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {formulas.map((f) => {
            const cost = costById(f.id);
            const open = expanded === f.id;
            return (
              <div key={f.id} className="bg-white border border-[#E5E7EB]" data-testid={`plastic-row-${f.id}`}>
                <div
                  onClick={() => setExpanded(open ? null : f.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-[#FFF8EE] transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <CaretRight size={16} weight="bold" className="text-[#4B5563] transition-transform"
                      style={{ transform: open ? "rotate(90deg)" : "none" }} />
                    <div>
                      <div className="font-display font-semibold text-base text-[#0A0A0A]">{f.name}</div>
                      <div className="text-xs text-[#4B5563] mt-0.5">
                        Output: <span className="font-mono-data">{f.output_product_name}</span> ·{" "}
                        Peso: <span className="font-mono-data">{f.peso_grammi} g</span> ·{" "}
                        Mat: <span className="font-mono-data">{f.material_name}</span> ·{" "}
                        <span className="font-mono-data">{f.produzione_oraria} pz/h</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {cost && (
                      <span className="font-mono-data text-xs text-[#F4A261] font-semibold bg-[#FEF5EB] px-2 py-1 border border-[#F4A261]/40"
                        title="Costo totale per pezzo"
                        data-testid={`plastic-cost-${f.id}`}>
                        € {cost.total_cost_per_piece.toFixed(4)} / pz
                      </span>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); openEdit(f); }}
                      data-testid={`edit-plastic-${f.id}`} className="text-[#4B5563] hover:text-[#1D3557] p-1" title="Modifica">
                      <PencilSimple size={16} weight="bold" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); duplicate(f); }}
                      data-testid={`duplicate-plastic-${f.id}`} className="text-[#4B5563] hover:text-[#2A9D8F] p-1" title="Duplica">
                      <Copy size={16} weight="bold" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); remove(f.id); }}
                      data-testid={`delete-plastic-${f.id}`} className="text-[#4B5563] hover:text-[#E63946] p-1" title="Elimina">
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
                {open && cost && (
                  <div className="border-t border-[#E5E7EB] bg-[#F9FAFB] p-5">
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="bg-white border border-[#E5E7EB] p-3">
                        <div className="text-[10px] uppercase tracking-wider text-[#4B5563] flex items-center gap-1">
                          <Drop size={10} weight="bold" /> Materia Prima
                        </div>
                        <div className="font-mono-data text-base font-bold text-[#0A0A0A]">€ {cost.material_cost.toFixed(4)}</div>
                        <div className="text-[10px] text-[#4B5563] mt-0.5">
                          {f.peso_grammi} g × €{f.cost_per_kg}/kg{f.scarto_percentuale ? ` (+${f.scarto_percentuale}% scarto)` : ""}
                        </div>
                      </div>
                      <div className="bg-white border border-[#E5E7EB] p-3">
                        <div className="text-[10px] uppercase tracking-wider text-[#4B5563] flex items-center gap-1">
                          <Wrench size={10} weight="bold" /> Manodopera
                        </div>
                        <div className="font-mono-data text-base font-bold text-[#0A0A0A]">€ {cost.labor_cost.toFixed(4)}</div>
                        <div className="text-[10px] text-[#4B5563] mt-0.5">€{f.costo_orario_lavoro}/h ÷ {f.produzione_oraria} pz/h</div>
                      </div>
                      <div className="bg-white border border-[#E5E7EB] p-3">
                        <div className="text-[10px] uppercase tracking-wider text-[#4B5563] flex items-center gap-1">
                          <Lightning size={10} weight="bold" /> Energia
                        </div>
                        <div className="font-mono-data text-base font-bold text-[#0A0A0A]">€ {cost.electricity_cost.toFixed(4)}</div>
                        <div className="text-[10px] text-[#4B5563] mt-0.5">{f.kwh_orari} kWh × €{f.costo_kwh}/kWh ÷ {f.produzione_oraria}</div>
                      </div>
                      <div className="bg-[#FEF5EB] border border-[#F4A261]/40 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-[#F4A261]">Costo Totale / pezzo</div>
                        <div className="font-mono-data text-lg font-bold text-[#F4A261]">€ {cost.total_cost_per_piece.toFixed(4)}</div>
                        <div className="text-[10px] text-[#4B5563] mt-0.5">
                          <Clock size={10} className="inline" /> 1h = {f.produzione_oraria} pz · costo orario macchina € {(cost.total_cost_per_piece * f.produzione_oraria).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    {(cost.components_breakdown || []).length > 0 && (
                      <table className="w-full bg-white border border-[#E5E7EB]">
                        <thead className="bg-[#F3F4F6]">
                          <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                            <th className="px-3 py-2 font-semibold">Componente accessorio</th>
                            <th className="px-3 py-2 font-semibold text-right">Quantità</th>
                            <th className="px-3 py-2 font-semibold text-right">Costo unit.</th>
                            <th className="px-3 py-2 font-semibold text-right">Totale</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cost.components_breakdown.map((c, i) => (
                            <tr key={i} className="border-t border-[#E5E7EB]">
                              <td className="px-3 py-2 text-sm">{c.product_name}</td>
                              <td className="px-3 py-2 text-right font-mono-data text-sm">{c.quantity} {c.unit}</td>
                              <td className="px-3 py-2 text-right font-mono-data text-xs text-[#4B5563]">€ {c.unit_cost.toFixed(4)}</td>
                              <td className="px-3 py-2 text-right font-mono-data text-sm font-semibold">€ {c.line_total.toFixed(4)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {f.notes && (
                      <div className="mt-3 text-xs text-[#4B5563]">
                        <b>Note:</b> {f.notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={creating} onOpenChange={(v) => { setCreating(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="new-plastic-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingId ? "Modifica Distinta Plastica" : "Nuova Distinta Plastica"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Nome distinta *</label>
              <input required data-testid="plastic-name-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261]"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Prodotto plastico *</label>
                <Select value={form.output_product_id} onValueChange={(v) => setForm({ ...form, output_product_id: v })}>
                  <SelectTrigger data-testid="plastic-output-select" className="rounded-none">
                    <SelectValue placeholder="Seleziona prodotto…" />
                  </SelectTrigger>
                  <SelectContent>
                    {outputCandidates.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Peso pezzo (g) *</label>
                <input type="number" step="0.01" min="0" required data-testid="plastic-weight-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261] font-mono-data"
                  value={form.peso_grammi} onChange={(e) => setForm({ ...form, peso_grammi: e.target.value })} />
              </div>
            </div>

            <div className="bg-[#FEF5EB] border border-[#F4A261]/30 p-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#F4A261] mb-2 flex items-center gap-1">
                <Drop size={12} weight="bold" /> Materia prima (polimero)
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Polietilene / Polimero</label>
                  <Select value={form.material_id} onValueChange={(v) => {
                    const p = materialCandidates.find((x) => x.id === v);
                    setForm({ ...form, material_id: v, cost_per_kg: p?.purchase_cost || form.cost_per_kg });
                  }}>
                    <SelectTrigger data-testid="plastic-material-select" className="rounded-none">
                      <SelectValue placeholder="HDPE, LDPE, PP, PET…" />
                    </SelectTrigger>
                    <SelectContent>
                      {materialCandidates.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} · €{p.purchase_cost}/kg</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">€ / kg</label>
                  <input type="number" step="0.01" min="0" data-testid="plastic-cost-per-kg-input"
                    className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261] font-mono-data"
                    value={form.cost_per_kg} onChange={(e) => setForm({ ...form, cost_per_kg: e.target.value })} />
                </div>
              </div>
              <div className="mt-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Scarto % (opz.)</label>
                <input type="number" step="0.1" min="0" max="100" data-testid="plastic-scrap-input"
                  className="w-32 border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261] font-mono-data"
                  placeholder="es. 2"
                  value={form.scarto_percentuale} onChange={(e) => setForm({ ...form, scarto_percentuale: e.target.value })} />
              </div>
            </div>

            <div className="bg-[#EEF2F7] border border-[#1D3557]/20 p-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] mb-2 flex items-center gap-1">
                <Clock size={12} weight="bold" /> Produzione & manodopera
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Produzione oraria (pz/h) *</label>
                  <input type="number" step="1" min="0" required data-testid="plastic-prod-rate-input"
                    className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                    value={form.produzione_oraria} onChange={(e) => setForm({ ...form, produzione_oraria: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Costo orario lavoro (€/h)</label>
                  <input type="number" step="0.01" min="0" data-testid="plastic-labor-cost-input"
                    className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                    value={form.costo_orario_lavoro} onChange={(e) => setForm({ ...form, costo_orario_lavoro: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="bg-[#FFF8E7] border border-[#FFC857]/40 p-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#B8860B] mb-2 flex items-center gap-1">
                <Lightning size={12} weight="bold" /> Energia elettrica
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Consumo orario (kWh/h)</label>
                  <input type="number" step="0.01" min="0" data-testid="plastic-kwh-input"
                    className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FFC857] font-mono-data"
                    value={form.kwh_orari} onChange={(e) => setForm({ ...form, kwh_orari: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Costo € / kWh</label>
                  <input type="number" step="0.001" min="0" data-testid="plastic-kwh-cost-input"
                    className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FFC857] font-mono-data"
                    placeholder="0.28"
                    value={form.costo_kwh} onChange={(e) => setForm({ ...form, costo_kwh: e.target.value })} />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">
                  Componenti accessori (opz.) — tappi, etichette, sottotappi
                </label>
                <button type="button" onClick={() => setForm({
                  ...form,
                  components: [...form.components, { product_id: "", quantity: 1, unit: "pz" }],
                })} data-testid="add-plastic-component"
                  className="text-xs font-display font-semibold uppercase tracking-wider text-[#1D3557] hover:text-[#2A4B7C] flex items-center gap-1">
                  <Plus size={12} weight="bold" /> Aggiungi
                </button>
              </div>
              <div className="space-y-2">
                {form.components.map((c, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`plastic-component-row-${i}`}>
                    <div className="flex-1">
                      <Select value={c.product_id} onValueChange={(v) => updateComponent(i, "product_id", v)}>
                        <SelectTrigger className="rounded-none">
                          <SelectValue placeholder="Seleziona componente" />
                        </SelectTrigger>
                        <SelectContent>
                          {componentCandidates.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name} · {p.unit}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <input type="number" step="0.01" placeholder="Qtà"
                      className="w-24 border border-[#E5E7EB] px-3 py-2 text-sm outline-none font-mono-data"
                      value={c.quantity} onChange={(e) => updateComponent(i, "quantity", e.target.value)} />
                    <span className="text-xs text-[#4B5563] font-mono-data w-8">{c.unit || "pz"}</span>
                    <button type="button" onClick={() => setForm({ ...form, components: form.components.filter((_, idx) => idx !== i) })}
                      className="text-[#4B5563] hover:text-[#E63946] p-2">
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Note</label>
              <textarea rows={2}
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261]"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            {/* Live cost preview */}
            <div className="bg-[#FEF5EB] border border-[#F4A261]/40 p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#F4A261] mb-2">
                Anteprima costo per pezzo
              </div>
              <div className="grid grid-cols-5 gap-2 font-mono-data text-xs">
                <div>
                  <div className="text-[10px] text-[#4B5563]">Materia</div>
                  <div className="text-[#0A0A0A] font-semibold">€ {livePreview.material.toFixed(4)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#4B5563]">Lavoro</div>
                  <div className="text-[#0A0A0A] font-semibold">€ {livePreview.labor.toFixed(4)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#4B5563]">Energia</div>
                  <div className="text-[#0A0A0A] font-semibold">€ {livePreview.electric.toFixed(4)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#4B5563]">Accessori</div>
                  <div className="text-[#0A0A0A] font-semibold">€ {livePreview.compTotal.toFixed(4)}</div>
                </div>
                <div className="border-l border-[#F4A261]/40 pl-2">
                  <div className="text-[10px] text-[#F4A261]">TOTALE</div>
                  <div className="text-[#F4A261] font-bold text-base" data-testid="plastic-live-total">
                    € {livePreview.total.toFixed(4)}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <button type="button" onClick={() => { setCreating(false); setEditingId(null); }}
                className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]">
                Annulla
              </button>
              <button type="submit" data-testid="submit-plastic-btn"
                className="bg-[#F4A261] hover:bg-[#E08C4A] text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider">
                {editingId ? "Salva Modifiche" : "Crea Distinta"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
